import React from 'react';
import { observer } from 'mobx-react';
import { observable, flow, reaction, action } from 'mobx';
import { Label } from 'reactstrap';
import { AvFeedback, AvForm, AvGroup, AvInput } from 'availity-mobx-reactstrap-validation';
import { Spring, animated } from 'react-spring';

import PageComponent from 'js/components/pages/PageComponent';
import AppContent from 'js/components/AppContent';
import Backdrop from 'js/components/core/Backdrop';
import Constraint from 'js/components/Constraint';

import analytics from 'js/services/Analytics';
import AppState from 'js/stores/AppState';
import AppUser from 'js/stores/AppUser';
import { makeRequest, errorResponse, navigate, url } from 'js/services/Network';
import { lightGrey1Color } from 'js/config/style';

import PageTitle from './PageTitle';
import Icon from './Icon';
import Message from './Message';
import Error from './Error';
import Button from './Button';
import InputText from 'js/components/common/LoginSignupDialog/Signup/InputText';
import Validated from 'js/components/common/LoginSignupDialog/Signup/Validated';

@observer
export default class ResetPasswordPage extends PageComponent {
  pageName = 'ResetPasswordPage';
  pageColor = lightGrey1Color;
  passwordInput = React.createRef();

  @observable new_password = '';
  @action
  setNewPassword(value) {
    this.new_password = value;
  }
  focusPasswordInput = () => this.passwordInput.current.focus();

  pageEvent = () => { /*analytics.page('Reset Password')*/ };

  constructor(props) {
    super(props);
    AppState.hideLoginSignup();
    this.loggedInReaction = reaction(() => AppUser.isLoggedIn, isLoggedIn => isLoggedIn && navigate(url('home-page')));
    this.state = {
      passwordFeedback: 'Please enter new password',
    };
  }

  onChange = (event) => {

    const newPassword = event.target.value;
    this.setNewPassword(newPassword);
    // Check for validation conditions and set feedback message
    if (!newPassword) {
      this.setState({ passwordFeedback: 'Password is required' });
    } else if (newPassword.length < 6) {
      this.setState({ passwordFeedback: 'Password must be at least 6 characters' });
    } else if (!/^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,72}$/.test(newPassword)) {
      this.setState({ passwordFeedback: 'Password must be 6-72 characters and include a number and a special character' });
    } else {
      this.setState({ passwordFeedback: '' });
    }
  }

  componentWillUnmount() {
    this.loggedInReaction();
  }

  @observable requestInProgress = false;
  @observable wasSuccessful = false;
  @observable error = null;

  submit = flow(function*(formValues) {
    this.error = null;

    if (!this.requestInProgress) {
      this.requestInProgress = true;
      formValues.token = this.props.token;

      try {
        yield makeRequest('reset-password', null, formValues);
        //analytics.track('Password Reset');
        this.wasSuccessful = true;
      } catch (error) {
        this.error = errorResponse(error);
      }

      this.requestInProgress = false;
    }
  });

  renderPage() {
    const { requestInProgress, error } = this;
    const offset = this.wasSuccessful ? '0rem' : '3rem';

    return (
      <AppContent padded>
        <Backdrop offset={offset}>
          <Constraint>
            <PageTitle>Password reset</PageTitle>
          </Constraint>
        </Backdrop>

        <Constraint offset={offset}>
          {this.wasSuccessful ? (
            <Spring native reset from={{ opacity: 0 }} to={{ opacity: 1 }}>
              {animation => (
                <animated.div style={animation}>
                  <Message>
                    <Icon name="check" />
                    <div>
                      <p>Your password has now been reset.</p>
                      <p>You can login using the navigation menu.</p>
                    </div>
                  </Message>
                </animated.div>
              )}
            </Spring>
          ) : (
            <Spring native from={{ opacity: 0 }} to={{ opacity: 1 }}>
              {animation => (
                <animated.div style={animation}>
                  <AvForm onValidSubmit={(event, values) => this.submit(values)}>
                    <AvGroup>
                      <Label for="email">Email address</Label>
                      <AvInput name="email" placeholder="email@address.com" required validate={{ email: true }} />
                    </AvGroup>

                    <AvGroup>
                      <Label for="password">Password</Label>
                      <AvInput name="password" onChange={this.onChange} type="password" maxLength={72} required
                      //          validate={{
                      //   required: { value: true, errorMessage: 'Password is required' },
                      //   pattern: { value: '^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,72}$', errorMessage: 'Password must be 6-72 characters and include a number and a special character' },
                      //   minLength: { value: 6, errorMessage: 'Password must be at least 6 characters' }
                      // }}
                      />
                      <>
                        <InputText value={this.new_password} onClick={this.focusPasswordInput}>
                          at least 6 characters, 1 number and 1 special character
                        </InputText>
                      </>
                      <AvFeedback> {this.state.passwordFeedback} </AvFeedback>
                      <>{this.state.passwordFeedback === '' && <Validated/>}</>
                    </AvGroup>

                    <AvGroup>
                      <Label for="password_confirmation">Password confirmation</Label>
                      <AvInput
                        name="password_confirmation"
                        required
                        type="password"
                        maxLength={72}
                        validate={{ match: { value: 'password' } }}
                      />
                    </AvGroup>

                    {error && <Error>{error}</Error>}

                    <Button isLoading={requestInProgress} type="submit">
                      {requestInProgress ? 'Saving password' : 'Save new password'}
                    </Button>
                  </AvForm>
                </animated.div>
              )}
            </Spring>
          )}
        </Constraint>
      </AppContent>
    );
  }
}
